import { useCallback, useContext } from 'react';
import { Action, Container, PointSchema } from '../../../types';
import { SortOption } from '../../components/Containers/ContainerTable';
import UserContext from '../../context/user';
import { REACT_APP_API_BASE_URL } from '../../env';
import { useProtectedRoute } from './useProtectedRoute';
import { usePublicRoute } from './usePublicRoute';
import useHandleError from '../useHandleError';

// A My Stack re-use request that clashes with one already pending/confirmed
// for the same container + booking ref (server 409 payload).
export type DuplicateReuseRequest = {
  containerCode: string;
  bookingRef: string;
  status: 'pending' | 'assigned' | 'internalUse' | 'submission';
  sameAccount: boolean;
  requestedBy?: string;
  requestedAt?: string;
};

export type ContainerOutcome = {
  _id: string;
  containerCode?: string;
  shippingLineCode?: string;
  accepted: boolean;
  requestDeclineReason?: string;
  decidedAt: string;
};

export default function useContainerApiRoutes() {
  const protectedRoute = useProtectedRoute();
  const handleError = useHandleError();
  const publicRoute = usePublicRoute();
  const { isLoggedIn } = useContext(UserContext);

  const getContainer = useCallback(
    async (id: string): Promise<Container> => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}`,
      });
      return response;
    },
    [protectedRoute]
  );

  const updateContainer = useCallback(
    async (container: Partial<Container>): Promise<Container> => {
      const response = await protectedRoute({
        method: 'put',
        url: `${REACT_APP_API_BASE_URL}/containers/${container._id}`,
        data: container,
      });
      return response;
    },
    [protectedRoute]
  );

  const getContainers = useCallback(
    async (
      currentPage = 0,
      itemsPerPage = 10,
      where: {
        shippingLineCode?: { $in: string[] };
        containerTypeCode?: { $in: string[] };
        client_id?: string;
        status?: { $in: string[] };
        assigned_to_user_id?: string;
        requested_by_user_id?: string;
        'client.user_id'?: string;
        availableTo?: { $gt: string };
        $or?: any[];
      } = {},
      sort: SortOption[] = [],
      near?: PointSchema
    ): Promise<{ containers: Container[]; totalItems: number } | undefined> => {
      const route = isLoggedIn ? protectedRoute : publicRoute;
      const response = await route({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers`,
        params: { itemsPerPage, currentPage, where, sort, near },
      });
      return response;
    },
    [isLoggedIn, protectedRoute, publicRoute]
  );

  // Creates containers. If any My Stack re-use request duplicates one that is
  // already pending/confirmed for the same container + booking ref, the server
  // answers 409 with the details instead of creating anything — the duplicate
  // cannot be forced through (a different booking ref is the legitimate path).
  const createContainers = useCallback(
    async (
      containers: Partial<Container>[]
    ): Promise<{ containers?: Container[]; duplicates?: DuplicateReuseRequest[] } | undefined> => {
      try {
        const response = await protectedRoute({
          method: 'post',
          url: `${REACT_APP_API_BASE_URL}/containers`,
          data: { containers },
          rethrowErrors: true,
        });
        return response;
      } catch (error: any) {
        if (error?.response?.status === 409 && error.response.data?.duplicates) {
          return { duplicates: error.response.data.duplicates as DuplicateReuseRequest[] };
        }
        handleError(error);
        return undefined;
      }
    },
    [protectedRoute, handleError]
  );

  const getContainerForExport = useCallback(
    async (where: { availableFrom: { $gte: string }; availableTo: { $lte: string } }): Promise<Container[]> => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers`,
        params: { where, isUnlimited: true },
      });
      return response.containers;
    },
    [protectedRoute]
  );

  const createClaimFutileTrip = useCallback(
    async (id: string, futileTrip: any) => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}/claim-futile-trips`,
        data: futileTrip,
      });
      return response;
    },
    [protectedRoute]
  );

  const confirmContainer = useCallback(
    async (container: Partial<Container>): Promise<Container> => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${container._id}/confirm`,
        data: container,
      });
      return response;
    },
    [protectedRoute]
  );

  // Reserve a container from the pool. A 409 means someone got there first,
  // it left the pool, or the caller is re-booking the same booking number —
  // surfaced as { unavailable } so the form can say so. Re-submitting with a
  // different booking number replaces the caller's own pending request.
  const createReserveRequest = useCallback(
    async (
      id: string,
      container: Partial<Container>
    ): Promise<Container | { unavailable: true; message: string; status?: string }> => {
      try {
        return await protectedRoute({
          method: 'post',
          url: `${REACT_APP_API_BASE_URL}/containers/${id}/request`,
          data: container,
          rethrowErrors: true,
        });
      } catch (error: any) {
        if (error?.response?.status === 409) {
          return {
            unavailable: true,
            message: error.response.data?.message,
            status: error.response.data?.status,
          };
        }
        handleError(error);
        throw error;
      }
    },
    [protectedRoute, handleError]
  );

  const deleteContainer = useCallback(
    async (id: string): Promise<Container> => {
      const response = await protectedRoute({
        method: 'delete',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}`,
      });
      return response;
    },
    [protectedRoute]
  );

  const cancelRequestContainer = useCallback(
    async (id: string, cancelRequest: any) => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}/cancel-requests`,
        data: cancelRequest,
      });
      return response;
    },
    [protectedRoute]
  );

  const createContainerDisputeAction = useCallback(
    async (id: string, disputeId: string, action: Pick<Action, 'actionType' | 'penaltyDollarFee'>) => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}/disputes/${disputeId}/actions`,
        data: { action },
      });
      return response;
    },
    [protectedRoute]
  );

  const getContainerDisputes = useCallback(
    async (userId: string, where: any) => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers/disputes/${userId}`,
        params: { ...where },
      });
      return response;
    },
    [protectedRoute]
  );

  const getAllContainers = useCallback(
    async (where?: any): Promise<Container[]> => {
      let params: any = { isUnlimited: true };
      if (where) {
        params = { ...params, where };
      }
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers`,
        params,
      });
      return response.containers;
    },
    [protectedRoute]
  );

  const emergencyCancellationContainer = useCallback(
    async (container: Container): Promise<Container> => {
      const response = await protectedRoute({
        method: 'patch',
        url: `${REACT_APP_API_BASE_URL}/containers/${container._id}/emergency-cancellations`,
      });
      return response;
    },
    [protectedRoute]
  );

  // Admin: permanently delete a re-use booking-request record (duplicates,
  // test entries). No emails; irreversible.
  const removeReuseRecord = useCallback(
    async (containerId: string): Promise<{ _id: string; removed: boolean }> => {
      const response = await protectedRoute({
        method: 'delete',
        url: `${REACT_APP_API_BASE_URL}/containers/${containerId}/reuse-record`,
      });
      return response;
    },
    [protectedRoute]
  );

  // Cancel a re-use request (pending or accepted) — e.g. the box was dehired
  // before it could be re-used. Emails the shipping line; container is archived.
  const cancelReuse = useCallback(
    async (containerId: string, reason: string): Promise<Container> => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${containerId}/reuse-cancellations`,
        data: { reason },
      });
      return response;
    },
    [protectedRoute]
  );

  // Admin: pending reuse-request queue.
  const getPendingContainers = useCallback(async (): Promise<Container[]> => {
    const response = await protectedRoute({
      method: 'get',
      url: `${REACT_APP_API_BASE_URL}/containers/pending`,
    });
    return response?.containers ?? [];
  }, [protectedRoute]);

  // Admin: accept a pending reuse request (assigns to the requester).
  const acceptContainerRequest = useCallback(
    async (id: string): Promise<Container> => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}/accept`,
      });
      return response;
    },
    [protectedRoute]
  );

  // Admin: decline a pending reuse request. `remove` pulls it from the pool.
  const declineContainerRequest = useCallback(
    async (id: string, reason: string, remove = false): Promise<Container> => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/${id}/decline`,
        data: { reason, remove },
      });
      return response;
    },
    [protectedRoute]
  );

  // Admin: reuse containers actioned for billing, filterable.
  const getReuseBilling = useCallback(
    async (filters: {
      from?: string;
      to?: string;
      requestedByUserId?: string;
      bookingRef?: string;
    }): Promise<Container[]> => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers/reuse-billing`,
        params: filters,
      });
      return response?.containers ?? [];
    },
    [protectedRoute]
  );

  // Admin: action a batch of pending reuse requests in one submit.
  const submitReuseDecisions = useCallback(
    async (
      decisions: { id: string; accept: boolean; reason?: string; remove?: boolean }[]
    ): Promise<Container[]> => {
      const response = await protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/containers/reuse-decisions`,
        data: { decisions },
      });
      return response;
    },
    [protectedRoute]
  );

  // Reuse requests of mine decided since `since` (ISO string).
  const getRecentOutcomes = useCallback(
    async (since: string): Promise<{ outcomes: ContainerOutcome[] }> => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/containers/recent-outcomes`,
        params: { since },
        silentErrors: true,
      });
      return { outcomes: response?.outcomes ?? [] };
    },
    [protectedRoute]
  );

  return {
    createContainers,
    getContainers,
    getContainer,
    getRecentOutcomes,
    updateContainer,
    getContainerForExport,
    createClaimFutileTrip,
    createReserveRequest,
    confirmContainer,
    deleteContainer,
    cancelRequestContainer,
    getAllContainers,
    createContainerDisputeAction,
    getContainerDisputes,
    emergencyCancellationContainer,
    cancelReuse,
    removeReuseRecord,
    getPendingContainers,
    acceptContainerRequest,
    declineContainerRequest,
    submitReuseDecisions,
    getReuseBilling,
  };
}
